import * as React from 'react';
import { useEffect, useState } from 'react';
import {
    AspectRatio,
    Box,
    Breadcrumbs,
    Card,
    CircularProgress,
    Link,
    Typography,
    Chip,
    Grid,
    Stack,
} from "@mui/joy";
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import { toast, ToastContainer } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';

import ApiService from "../../axios/Bookings-Axios";

interface BookingImage {
    id: number;
    booking: number;
    before_image: string | null;
    after_image: string | null;
    uploaded_at: string;
    booking_customer_name?: string;
    booking_date?: string;
}

export default function Media() {
    const [images, setImages] = useState<BookingImage[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        ApiService.getAllBookingImages()
            .then(data => {
                setImages(data);
            })
            .catch(error => {
                console.error("Failed to fetch media:", error);
                toast.error("Could not load images. " + (error.response?.data?.error || "Please try again."));
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    return (
        <>
            <ToastContainer position="top-right" autoClose={4000} theme="dark" />
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Breadcrumbs size="sm" aria-label="breadcrumbs" separator={<ChevronRightRoundedIcon />} sx={{ pl: 0 }}>
                    <Link underline="none" color="neutral" href="/dashboard" aria-label="Home">
                        <HomeRoundedIcon />
                    </Link>
                    <Link underline="hover" color="neutral" href="/dashboard" fontSize={12} fontWeight={500}>
                        Dashboard
                    </Link>
                    <Typography color="primary" fontWeight={500} fontSize={12}>
                        Media Gallery
                    </Typography>
                </Breadcrumbs>
            </Box>

            <Box sx={{ mb: 2 }}>
                <Typography level="h2" component="h1">
                    Media Gallery
                </Typography>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
                    <CircularProgress size="lg" />
                </Box>
            ) : (
                <Box
                    sx={{
                        flexGrow: 1,
                        overflowY: 'auto',
                        p: 1,
                        maxHeight: 'calc(100vh - 180px)'
                    }}
                >
                    {images.length === 0 && !loading && (
                        <Typography sx={{ textAlign: 'center', mt: 4 }}>
                            No images found for the selected criteria.
                        </Typography>
                    )}
                    <Grid container spacing={2}>
                        {images.map((imgRecord) => (
                            <Grid key={`record-${imgRecord.id}`} xs={12} sm={6} md={4} lg={3}>
                                <Card
                                    variant="outlined"
                                    sx={{
                                        height: '100%',
                                        '&:hover': {
                                            boxShadow: 'md',
                                            borderColor: 'neutral.outlinedHoverBorder',
                                            transform: 'translateY(-2px)',
                                            transition: 'all 0.2s ease'
                                        }
                                    }}
                                >
                                    <Typography level="title-sm" sx={{ mb: 1 }}>
                                        Booking #{imgRecord.booking}
                                    </Typography>

                                    <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
                                        <Box sx={{ flex: 1 }}>
                                            <Chip size="sm" variant="soft" color="primary" sx={{ mb: 1, width: '100%', justifyContent: 'center' }}>Before</Chip>
                                            {imgRecord.before_image ? (
                                                <AspectRatio ratio="1/1">
                                                    <img
                                                        src={imgRecord.before_image}
                                                        alt={`Before - Booking ${imgRecord.booking}`}
                                                        loading="lazy"
                                                        style={{
                                                            objectFit: 'cover',
                                                            width: '100%',
                                                            height: '100%'
                                                        }}
                                                    />
                                                </AspectRatio>
                                            ) : (
                                                <Box sx={{
                                                    height: 150,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    bgcolor: 'background.level1',
                                                    borderRadius: 'sm'
                                                }}>
                                                    <BrokenImageIcon sx={{ fontSize: 40, color: 'text.tertiary' }}/>
                                                    <Typography level="body-xs" sx={{ mt: 1 }}>No Image</Typography>
                                                </Box>
                                            )}
                                        </Box>

                                        <Box sx={{ flex: 1 }}>
                                            <Chip size="sm" variant="soft" color="success" sx={{ mb: 1, width: '100%', justifyContent: 'center' }}>After</Chip>
                                            {imgRecord.after_image ? (
                                                <AspectRatio ratio="1/1">
                                                    <img
                                                        src={imgRecord.after_image}
                                                        alt={`After - Booking ${imgRecord.booking}`}
                                                        loading="lazy"
                                                        style={{
                                                            objectFit: 'cover',
                                                            width: '100%',
                                                            height: '100%'
                                                        }}
                                                    />
                                                </AspectRatio>
                                            ) : (
                                                <Box sx={{
                                                    height: 150,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    bgcolor: 'background.level1',
                                                    borderRadius: 'sm'
                                                }}>
                                                    <BrokenImageIcon sx={{ fontSize: 40, color: 'text.tertiary' }}/>
                                                    <Typography level="body-xs" sx={{ mt: 1 }}>No Image</Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    </Stack>

                                    {imgRecord.booking_date && (
                                        <Typography level="body-xs" sx={{ mt: 1, textAlign: 'center' }}>
                                            {new Date(imgRecord.booking_date).toLocaleDateString()}
                                        </Typography>
                                    )}
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            )}
        </>
    );
}